import React, { useState } from 'react';
import { MultiselectAnswer, PreferenceLevel, QuestionOption } from './types';
import ConfirmationModal from '~/modals/ConfirmationModal';
import PreferenceButtons from './PreferenceButtons';
import { applyToAll, uniformLevel, wouldOverwrite } from './bulkPreference';

/**
 * One input column. A plain multiselect question has a single column; a
 * per-segment question has one column per selected market segment.
 */
export interface PreferenceColumn {
  id: string;
  /** Column header — only set for per-segment columns. */
  header?: string;
  value: MultiselectAnswer;
  onChange: (next: MultiselectAnswer) => void;
}

/**
 * A child preference row rendered indented beneath one of the parent
 * options. Each child row has its own per-column input — the column ids
 * match the parent's `columns`, so the nested inputs align with their
 * parent's columns one-for-one.
 */
export interface NestedRow {
  /** Stable key (typically `${childQuestionKey}:${childOptionValue}`). */
  id: string;
  /** Which parent option this row sits under. */
  parentOptionValue: string;
  label: string;
  /** Per-column read/write for this nested row. Keyed by parent column id. */
  columns: Record<
    string,
    {
      value: PreferenceLevel | undefined;
      onChange: (next: PreferenceLevel | undefined) => void;
    }
  >;
}

interface Props {
  options: QuestionOption[];
  columns: PreferenceColumn[];
  disabled?: boolean;
  /**
   * When options carry a `group`, the sub-heading to show above each group
   * (group value → label, e.g. state code → state name). A group with no entry
   * falls back to the raw group value. Omit for ungrouped questions.
   */
  groupLabels?: Record<string, string>;
  /**
   * Nested sub-rows, rendered indented after the matching parent option
   * row, but only when the parent option is non-'no' in at least one
   * column. Sub-rows whose parent option is fully 'no' / unanswered stay
   * hidden so the form doesn't ask buyers to grade an option they ruled
   * out.
   */
  nestedRows?: NestedRow[];
}

/**
 * Three-tier Yes/Maybe/No selector for a multiselect question. One row per
 * option; the label sits left and the input column(s) sit right via
 * space-between. Because every input group is a fixed width, the right-hand
 * cluster is identical on every row, so the columns line up — one column or
 * many. Per-segment questions just add more columns.
 */
interface PendingSelectAll {
  column: PreferenceColumn;
  level: PreferenceLevel | undefined;
}

const PreferenceSelector: React.FC<Props> = ({
  options,
  columns,
  disabled,
  nestedRows,
  groupLabels,
}) => {
  // Group nested rows by their parent option for O(1) lookup in the render
  // loop below.
  const nestedByParent = React.useMemo(() => {
    const map = new Map<string, NestedRow[]>();
    for (const row of nestedRows ?? []) {
      const list = map.get(row.parentOptionValue) ?? [];
      list.push(row);
      map.set(row.parentOptionValue, list);
    }
    return map;
  }, [nestedRows]);
  const hasHeaders = columns.some((c) => c.header);
  const multi = columns.length > 1;

  // A "Select all" click that would overwrite existing answers, held until
  // the buyer confirms it in the modal below.
  const [pending, setPending] = useState<PendingSelectAll | null>(null);

  const handleSelectAll = (column: PreferenceColumn, level: PreferenceLevel | undefined) => {
    if (wouldOverwrite(column.value, options, level)) {
      setPending({ column, level });
    } else {
      column.onChange(applyToAll(options, level));
    }
  };

  const confirmPending = () => {
    if (pending) pending.column.onChange(applyToAll(options, pending.level));
    setPending(null);
  };

  const setOption = (
    column: PreferenceColumn,
    optionValue: string,
    level: PreferenceLevel | undefined
  ) => {
    const next = { ...column.value };
    if (level === undefined) {
      delete next[optionValue];
    } else {
      next[optionValue] = level;
    }
    column.onChange(next);
  };

  return (
    <div
      className={
        'buyer-profile-preference-selector' +
        (multi ? ' buyer-profile-preference-selector--multi' : '')
      }
    >
      {hasHeaders && (
        <div className="buyer-profile-preference-row buyer-profile-preference-row--headers">
          <span className="buyer-profile-preference-label" />
          <div className="buyer-profile-input-cluster">
            {columns.map((column) => (
              <div className="buyer-profile-input-col" key={column.id}>
                <span className="buyer-profile-col-header">{column.header}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="buyer-profile-preference-row buyer-profile-preference-row--select-all">
        <span className="buyer-profile-preference-label">Select all</span>
        <div className="buyer-profile-input-cluster">
          {columns.map((column) => (
            <div className="buyer-profile-input-col" key={column.id}>
              <PreferenceButtons
                value={uniformLevel(column.value, options)}
                disabled={disabled}
                onChange={(level) => handleSelectAll(column, level)}
              />
            </div>
          ))}
        </div>
      </div>

      {options.map((option, i) => {
        const childRows = nestedByParent.get(option.value) ?? [];
        // A nested group is only meaningful when the parent option is
        // actually a yes / maybe somewhere — otherwise the buyer ruled it
        // out and we don't want to ask sub-questions about it.
        const parentSelectedSomewhere = columns.some((c) => {
          const pref = c.value[option.value];
          return pref === 'yes' || pref === 'maybe';
        });
        // For grouped options (e.g. utility zones by state), emit a sub-heading
        // whenever the group changes from the previous option.
        const prevGroup = i > 0 ? options[i - 1].group : undefined;
        const showGroupHeading =
          option.group !== undefined && option.group !== prevGroup;
        return (
          <React.Fragment key={option.value}>
            {showGroupHeading && (
              <div className="buyer-profile-preference-group-heading">
                {groupLabels?.[option.group!] ?? option.group}
              </div>
            )}
            <div className="buyer-profile-preference-row">
              <span className="buyer-profile-preference-label">{option.label}</span>
              <div className="buyer-profile-input-cluster">
                {columns.map((column) => (
                  <div className="buyer-profile-input-col" key={column.id}>
                    <PreferenceButtons
                      value={column.value[option.value]}
                      disabled={disabled}
                      onChange={(level) => setOption(column, option.value, level)}
                    />
                  </div>
                ))}
              </div>
            </div>
            {parentSelectedSomewhere &&
              childRows.map((row) => (
                <div
                  className="buyer-profile-preference-row buyer-profile-preference-row--nested"
                  key={row.id}
                >
                  <span className="buyer-profile-preference-label buyer-profile-preference-label--nested">
                    <span className="buyer-profile-nested-connector" aria-hidden>
                      └
                    </span>
                    {row.label}
                  </span>
                  <div className="buyer-profile-input-cluster">
                    {columns.map((column) => {
                      const cell = row.columns[column.id];
                      // A parent column where this option is `no` (or
                      // unanswered) shouldn't accept a sub-stage answer —
                      // dim those cells, but keep them present so the grid
                      // alignment is preserved across columns.
                      const parentLevel = column.value[option.value];
                      const columnActive =
                        parentLevel === 'yes' || parentLevel === 'maybe';
                      return (
                        <div className="buyer-profile-input-col" key={column.id}>
                          {cell && (
                            <PreferenceButtons
                              value={cell.value}
                              disabled={disabled || !columnActive}
                              onChange={cell.onChange}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </React.Fragment>
        );
      })}

      {pending && (
        <ConfirmationModal
          titleText="Overwrite existing answers?"
          contentElement={
            <p>
              Selecting all will replace the answers you&apos;ve already set in this
              section. Do you want to continue?
            </p>
          }
          confirmText="Overwrite"
          status="idle"
          mutate={confirmPending}
          closeModal={() => setPending(null)}
        />
      )}
    </div>
  );
};

export default PreferenceSelector;
